import React from "react";

/**
 * Generic form field wrapper.
 * Props:
 * - id, name, label, value, onChange, placeholder, type
 * - as: "input" | "select" | "textarea"
 * - options: [{ value, label }] (for selects)
 * - error: string
 * - required: boolean
 * - className: extra class names for the control
 */
const FormField = ({
  id,
  name,
  label,
  value,
  onChange,
  placeholder,
  type = "text",
  as = "input",
  options = [],
  error,
  required = false,
  className = "",
  ...rest
}) => {
  const controlBaseClass =
    "w-full border-[#D0D0D0] border-2 px-4 py-4 rounded-xl outline-none text-left";

  return (
    <label
      htmlFor={id || name}
      className="flex flex-col"
    >
      {error && (
        <p className="text-red-600 text-sm mb-1" role="alert">
          {error}
        </p>
      )}

      <span className="text-sm font-medium mb-1">
        {label}
        {required && <span aria-hidden="true"> *</span>}
      </span>

      {as === "select" ? (
        <select
          id={id || name}
          name={name}
          value={value}
          onChange={onChange}
          className={`${controlBaseClass} ${className}`}
          aria-invalid={!!error}
          {...rest}
        >
          {options.length === 0 && <option value="">Select</option>}
          {options.map((opt) => (
            <option
              key={opt.value}
              value={opt.value}
            >
              {opt.label}
            </option>
          ))}
        </select>
      ) : as === "textarea" ? (
        <textarea
          id={id || name}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className={`${controlBaseClass} ${className} resize-none`}
          aria-invalid={!!error}
          {...rest}
        />
      ) : (
        <input
          id={id || name}
          name={name}
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className={`${controlBaseClass} ${className}`}
          aria-invalid={!!error}
          {...rest}
        />
      )}
    </label>
  );
};

export default FormField;
